/* ======== UI ========= */
import { configureFonts } from "react-native-paper";

import { buildType } from "@helpers/expo-extras";
import { useAppStore } from "@hooks/useAppStore";
/* ======== Helpers ========= */
import { typography } from "@memorang/helpers/src/font";
import merge from "lodash-es/merge";
import type { Apps } from "types/apps";
import { breakpoints, componentOverrides, fontConfig } from "./constants";
import { getCustomPalette, getMergedCustomTheme, textColors } from "./helpers";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const buildTheme = (appName: Apps, darkMode: boolean) => {
	const appFont = useAppStore.getState().app?.font;
	const customTextColors = textColors(darkMode);
	const customPalette = getCustomPalette(darkMode);
	const mergedCustomTheme = getMergedCustomTheme({
		appName,
		darkMode,
	});
	const customTypography = typography(darkMode, appFont ?? "opensans");
	const fonts = configureFonts({ config: fontConfig });
	const colors = {
		...merge(customPalette, mergedCustomTheme.colors),
		text: darkMode ? "#fff" : "#000",
		white: "#fff",
		black: "#000",
	};

	return {
		...mergedCustomTheme,
		dark: darkMode,
		colors,
		palette: {
			text: {
				...customTextColors,
			},
			calculator: {
				background: "#2a2a2a",
				displayBackground: "#404040",
				inputTextColor: "#00ff00",
				operationTextColor: "#00ffff",
				resultTextColor: "#ffff00",
				memoryTextColor: "#ff9900",
			},

			yellow: {
				main: darkMode ? "#FFEB3B" : "#FBC02D",
				light: darkMode ? "#FFFF72" : "rgba(240, 255, 150, 0.3)",
				dark: darkMode ? "#B2A300" : "#F57F17",
			},
			success: {
				main: darkMode ? "#4CAF50" : "#2E7D32",
				light: darkMode ? "#81C784" : "#2E7D32",
				dark: darkMode ? "#388E3C" : "#1B5E20",
				info: darkMode ? "rgba(102, 187, 104, 1)" : "rgba(187, 240, 181, 1)",
			},
			error: {
				main: darkMode ? "#FF4D4D" : "#EB0014",
				light: darkMode ? "#FF9999" : "#EB0014",
				dark: darkMode ? "#CC0000" : "#C70011",
				info: darkMode ? "rgba(255, 180, 180, 1)" : "rgba(255, 220, 220, 1)",
			},
			purple: {
				main: darkMode ? "#B964F7" : "#800080",
				light: darkMode ? "#D4A4F7" : "#800080",
				dark: darkMode ? "#9932CC" : "#4B0082",
			},
			highlight: {
				main: "#FFDACA",
				light: "#F1FF91",
				quote: "#FF8F60",
			},
			chips: {
				free: "#81C784",
				premium: "#FFD700",
			},
			info: {
				main: darkMode ? "#0C4860" : "rgba(200, 239, 255, 1)",
			},
		},
		breakpoints,
		typography: customTypography,
		fonts,
		componentOverrides,
	};
};

const theme = (darkMode?: boolean) => buildTheme(buildType, darkMode!);
export default theme;
